import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAppSelector } from '../store/hooks.ts';
import { selectCurrentOrganization } from '../store/organization/organizationSlice.ts';
import {
  listBucketConfigs,
  createBucketConfig,
  updateBucketConfig,
  deleteBucketConfig,
  testBucketConfig,
} from '../api.ts';
import { BucketConfig, BucketConfigCreate, BucketType } from '../models/bucketConfig.ts';

const BucketConfigManagement = () => {
  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const [bucketConfigs, setBucketConfigs] = useState<BucketConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add bucket dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newBucketType, setNewBucketType] = useState<string>(BucketType.MANAGED);
  const [newBucketName, setNewBucketName] = useState('');
  const [newEndpointUrl, setNewEndpointUrl] = useState('');
  const [newAccessKeyId, setNewAccessKeyId] = useState('');
  const [newSecretAccessKey, setNewSecretAccessKey] = useState('');
  const [newRegion, setNewRegion] = useState('');
  const [newIsDefault, setNewIsDefault] = useState(false);

  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingBucket, setDeletingBucket] = useState<BucketConfig | null>(null);

  // Test connection state
  const [testingBuckets, setTestingBuckets] = useState<Set<number>>(new Set());

  const isAdmin = currentOrganization?.role === 'admin' || currentOrganization?.role === 'owner';

  const loadBucketConfigs = useCallback(async () => {
    if (!currentOrganization?.id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await listBucketConfigs(currentOrganization.id);
      setBucketConfigs(data);
    } catch (err: any) {
      console.error('Failed to load bucket configs:', err);
      setError(err.response?.data?.detail || 'Failed to load bucket configurations');
    } finally {
      setLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (currentOrganization) {
      loadBucketConfigs();
    }
  }, [currentOrganization, loadBucketConfigs]);

  const resetAddForm = () => {
    setNewBucketType(BucketType.MANAGED);
    setNewBucketName('');
    setNewEndpointUrl('');
    setNewAccessKeyId('');
    setNewSecretAccessKey('');
    setNewRegion('');
    // Auto-check only if this will be the first bucket, else start unchecked
    setNewIsDefault(bucketConfigs.length === 0);
  };

  const handleAddBucket = async () => {
    if (!currentOrganization?.id) return;

    if (newBucketType === BucketType.USER_PROVIDED) {
      if (!newBucketName.trim() || !newEndpointUrl.trim() || !newAccessKeyId.trim() || !newSecretAccessKey.trim()) {
        setError('All fields are required for user-provided buckets');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const bucketData: BucketConfigCreate = {
        bucket_type: newBucketType,
        // Respect user choice even for first bucket now
        is_default: newIsDefault,
      };

      if (newBucketType === BucketType.USER_PROVIDED) {
        bucketData.bucket_name = newBucketName;
        bucketData.endpoint_url = newEndpointUrl;
        bucketData.access_key_id = newAccessKeyId;
        bucketData.secret_access_key = newSecretAccessKey;
        bucketData.region = newRegion || null;
      }

      await createBucketConfig(currentOrganization.id, bucketData);
      setSuccess('Bucket configuration added successfully');
      setShowAddDialog(false);
      resetAddForm();
      await loadBucketConfigs();
    } catch (err: any) {
      console.error('Failed to add bucket config:', err);
      setError(err.response?.data?.detail || 'Failed to add bucket configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (config: BucketConfig) => {
    if (!currentOrganization?.id || config.is_default) return;

    try {
      setLoading(true);
      setError(null);
      await updateBucketConfig(currentOrganization.id, config.id, { is_default: true });
      setSuccess('Default bucket updated successfully');
      await loadBucketConfigs();
    } catch (err: any) {
      console.error('Failed to set default bucket:', err);
      setError(err.response?.data?.detail || 'Failed to set default bucket');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (config: BucketConfig) => {
    if (!currentOrganization?.id) return;

    try {
      setLoading(true);
      setError(null);
      await updateBucketConfig(currentOrganization.id, config.id, { is_active: !config.is_active });
      setSuccess(`Bucket ${config.is_active ? 'deactivated' : 'activated'} successfully`);
      await loadBucketConfigs();
    } catch (err: any) {
      console.error('Failed to toggle bucket status:', err);
      setError(err.response?.data?.detail || 'Failed to update bucket status');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBucket = async () => {
    if (!currentOrganization?.id || !deletingBucket) return;

    try {
      setLoading(true);
      setError(null);
      await deleteBucketConfig(currentOrganization.id, deletingBucket.id);
      setSuccess('Bucket configuration deleted successfully');
      setShowDeleteDialog(false);
      setDeletingBucket(null);
      await loadBucketConfigs();
    } catch (err: any) {
      console.error('Failed to delete bucket config:', err);
      setError(err.response?.data?.detail || 'Failed to delete bucket configuration');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (config: BucketConfig) => {
    setDeletingBucket(config);
    setShowDeleteDialog(true);
  };

  const handleTestBucket = async (config: BucketConfig) => {
    if (!currentOrganization?.id) return;

    try {
      // Track which bucket is being tested
      setTestingBuckets(prev => new Set(prev).add(config.id));
      setError(null);

      const result = await testBucketConfig(currentOrganization.id, config.id);

      if (result.success) {
        setSuccess(result.message);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      console.error('Failed to test bucket:', err);
      setError(err.response?.data?.detail || 'Failed to test bucket connection');
    } finally {
      // Remove from testing set
      setTestingBuckets(prev => {
        const newSet = new Set(prev);
        newSet.delete(config.id);
        return newSet;
      });
    }
  };

  const getBucketTypeColor = (type: string) => {
    return type === BucketType.MANAGED ? 'primary' : 'secondary';
  };

  if (!currentOrganization) {
    return (
      <Alert severity="warning">
        Please select an organization to manage bucket configurations.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Storage Buckets
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setNewIsDefault(bucketConfigs.length === 0); setShowAddDialog(true); }}
            disabled={loading}
          >
            Add Bucket
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You need admin or owner role to manage storage buckets.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Bucket Name</TableCell>
              <TableCell>Endpoint</TableCell>
              <TableCell>Access Key ID</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Default</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && bucketConfigs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : bucketConfigs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                  No bucket configurations found
                </TableCell>
              </TableRow>
            ) : (
              bucketConfigs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>
                    <Chip
                      label={config.bucket_type === BucketType.MANAGED ? 'Managed' : 'User Provided'}
                      color={getBucketTypeColor(config.bucket_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {config.bucket_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {config.endpoint_url ? (
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {config.endpoint_url}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Default
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {config.access_key_id ? (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {config.access_key_id}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        System
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {config.region || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.is_active ? 'Active' : 'Inactive'}
                      color={config.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {config.is_default ? (
                      <StarIcon color="primary" fontSize="small" />
                    ) : (
                      isAdmin && (
                        <IconButton
                          size="small"
                          onClick={() => handleSetDefault(config)}
                          disabled={loading}
                          title="Set as default"
                        >
                          <StarBorderIcon fontSize="small" />
                        </IconButton>
                      )
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleTestBucket(config)}
                        disabled={testingBuckets.has(config.id)}
                        sx={{ mr: 1 }}
                      >
                        {testingBuckets.has(config.id) ? 'Testing...' : 'Test'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleToggleActive(config)}
                        disabled={loading}
                        sx={{ mr: 1 }}
                      >
                        {config.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(config)}
                        disabled={loading}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Bucket Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Storage Bucket</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Choose between a managed bucket (created automatically) or provide your own S3-compatible storage.
          </Alert>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Bucket Type</InputLabel>
            <Select
              value={newBucketType}
              label="Bucket Type"
              onChange={(e: SelectChangeEvent) => setNewBucketType(e.target.value)}
              disabled={loading}
            >
              <MenuItem value={BucketType.MANAGED}>Managed (Auto-created)</MenuItem>
              <MenuItem value={BucketType.USER_PROVIDED}>User Provided (S3-compatible)</MenuItem>
            </Select>
          </FormControl>

          {newBucketType === BucketType.MANAGED && currentOrganization && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Bucket will be created automatically
              </Typography>
              <Typography variant="body2">
                Bucket name: <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  robustrisk-{currentOrganization.slug}
                </Typography>
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                The bucket will be created in the system storage and configured automatically.
              </Typography>
            </Alert>
          )}

          {newBucketType === BucketType.USER_PROVIDED && (
            <>
              <TextField
                margin="dense"
                label="Bucket Name"
                type="text"
                fullWidth
                variant="outlined"
                value={newBucketName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewBucketName(e.target.value)}
                disabled={loading}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                margin="dense"
                label="Endpoint URL"
                type="text"
                fullWidth
                variant="outlined"
                value={newEndpointUrl}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewEndpointUrl(e.target.value)}
                disabled={loading}
                placeholder="https://s3.amazonaws.com"
                required
                sx={{ mb: 2 }}
              />

              <TextField
                margin="dense"
                label="Access Key ID"
                type="text"
                fullWidth
                variant="outlined"
                value={newAccessKeyId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewAccessKeyId(e.target.value)}
                disabled={loading}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                margin="dense"
                label="Secret Access Key"
                type="password"
                fullWidth
                variant="outlined"
                value={newSecretAccessKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSecretAccessKey(e.target.value)}
                disabled={loading}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                margin="dense"
                label="Region (optional)"
                type="text"
                fullWidth
                variant="outlined"
                value={newRegion}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRegion(e.target.value)}
                disabled={loading}
                placeholder="us-east-1"
                sx={{ mb: 2 }}
              />
            </>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={newIsDefault}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewIsDefault(e.target.checked)}
                disabled={loading}
              />
            }
            label={'Set as default bucket'}
          />
          {bucketConfigs.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0, mt: 1, display: 'block' }}>
              You can optionally make the first bucket default now or later.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddBucket}
            variant="contained"
            disabled={loading}
          >
            Add Bucket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Bucket Configuration</DialogTitle>
        <DialogContent>
          {deletingBucket && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will only remove the bucket configuration, not the actual bucket or its contents.
              </Alert>
              <Typography>
                Are you sure you want to delete the bucket configuration for{' '}
                <Typography component="span" fontWeight="bold">
                  {deletingBucket.bucket_name}
                </Typography>
                ?
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteBucket}
            variant="contained"
            color="error"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BucketConfigManagement;

